import {
  AssessmentEntry,
  AssessmentMethodType,
  AssessmentsGrade,
  MarkbookAssessmentEntry,
} from '@schooly/api';
import {
  CellContext,
  enhanceGridCellHandler,
  GridCellMiddleware,
} from '@schooly/components/data-grid';
import React, { useCallback } from 'react';

import { AssessmentEntryMethod } from '../../../../components/uikit-components/AssessmentEntryMethods/AssessmentEntryMethod';
import {
  assessmentMethodTypes,
  getMarkbookForGroupAssessmentParams,
} from './assessmentEntryUpdates';
import {
  AssessmentCellState,
  RenderMarkbookForGroupMethodProps,
  RenderMarkbookForGroupsMethodProps,
  UpdateAssessmentEntryParams,
  UpdateMarkbookForGroupsAssessmentEntryParams,
} from './types';

export const useRenderBaseMarkbookForGroupEntryMethod = (
  groupId: string,
  lists: Record<string, AssessmentsGrade>,
  onSuccess?: () => void,
) =>
  useCallback(
    ({
      student,
      method,
      scoreControllerProps,
      gradeControllerProps,
      commentControllerProps,
      assessmentEntries,
      updateAssessmentEntry,
    }: RenderMarkbookForGroupMethodProps & {
      updateAssessmentEntry?: (params: UpdateAssessmentEntryParams) => Promise<void>;
    }) => {
      const entries = getMarkbookForGroupAssessmentParams(
        student.user.relation_id,
        method,
        assessmentEntries,
      );

      return React.createElement(AssessmentEntryMethod, {
        relationId: student.user?.relation_id,
        groupId: groupId,
        method: method,
        assessmentId: assessmentEntries.id,
        enterable: assessmentEntries.enterable_and_my_group,
        entries: entries?.entries ?? [],
        lists: lists,
        scoreControllerProps: scoreControllerProps,
        gradeControllerProps: gradeControllerProps,
        commentControllerProps: commentControllerProps,
        onSuccess: onSuccess,
        updateAssessmentEntry,
      });
    },
    [groupId, lists, onSuccess],
  );

const updateAssessmentEntry = <T extends UpdateAssessmentEntryParams>({
  updateEntryParams,
  onUpdateEntry,
  cellContext,
  middlewareConfig,
  cellId,
}: UpdateAssessmentEntryParamsWithCellId<T>) => {
  const { studentId, methodId, comments, methodType } = updateEntryParams;

  if (!cellId) {
    console.warn('⚠️ No cellId provided for middleware');
    return Promise.resolve();
  }

  if (assessmentMethodTypes.isScore(methodType)) {
    const enhancedHandler = enhanceGridCellHandler(
      onUpdateEntry,
      cellContext,
      middlewareConfig[AssessmentMethodType.Score],
    );

    return enhancedHandler.withCellId(cellId, studentId, methodId)(updateEntryParams);
  }
  if (assessmentMethodTypes.isGrade(methodType)) {
    const enhancedHandler = enhanceGridCellHandler(
      onUpdateEntry,
      cellContext,
      middlewareConfig[AssessmentMethodType.Grade],
    );
    return enhancedHandler.withCellId(cellId, studentId, methodId)(updateEntryParams);
  }
  if (comments && comments.length > 0 && comments[0]?.comment) {
    const enhancedHandler = enhanceGridCellHandler(
      onUpdateEntry,
      cellContext,
      middlewareConfig[AssessmentMethodType.Comment],
    );

    return enhancedHandler.withCellId(cellId, studentId, methodId)(updateEntryParams);
  }

  return Promise.resolve();
};

export const useRenderMarkbookForGroupEntryMethodWithMiddleware = (
  groupId: string,
  lists: Record<string, AssessmentsGrade>,
  onUpdateEntry: (params: UpdateAssessmentEntryParams) => Promise<void>,
  cellContext: CellContext<AssessmentCellState>,
  middlewareConfig: Record<
    AssessmentMethodType,
    GridCellMiddleware<any, any, AssessmentCellState>[]
  >,
  onSuccess?: () => void,
) => {
  const baseRenderEntryMethod = useRenderBaseMarkbookForGroupEntryMethod(groupId, lists, onSuccess);

  return useCallback(
    (props: RenderMarkbookForGroupMethodProps) => {
      const updateEntry = (params: UpdateAssessmentEntryParams) =>
        updateAssessmentEntry({
          updateEntryParams: params,
          onUpdateEntry,
          cellContext,
          middlewareConfig,
          cellId: props.cellId,
        });

      return baseRenderEntryMethod({ ...props, updateAssessmentEntry: updateEntry });
    },
    [baseRenderEntryMethod, onUpdateEntry, cellContext, middlewareConfig],
  );
};

export const useRenderBaseMarkbookForGroupsEntryMethod = (
  lists: Record<string, AssessmentsGrade>,
  onSuccess?: () => void,
) =>
  useCallback(
    ({
      student,
      assessmentEntries,
      updateAssessmentEntry,
      categoryId,
      ...props
    }: RenderMarkbookForGroupsMethodProps & {
      updateAssessmentEntry?: (
        params: UpdateMarkbookForGroupsAssessmentEntryParams,
      ) => Promise<void>;
    }) => {
      const { entries, groupId, enterableAndMyGroup } =
        getAssessmentEntriesDataFromMarkbookEntries(assessmentEntries);
      const updateEntry = updateAssessmentEntry
        ? (params: UpdateAssessmentEntryParams) => {
            updateAssessmentEntry({ categoryId, ...params });
          }
        : undefined;

      return React.createElement(AssessmentEntryMethod, {
        relationId: student.relation_id,
        lists: lists,
        enterable: enterableAndMyGroup,
        onSuccess,
        entries,
        groupId,
        updateAssessmentEntry: updateEntry,
        ...props,
      });
    },
    [lists, onSuccess],
  );

export type UseRenderMarkbookForGroupsEntryMethodWithMiddlewareProps = {
  lists: Record<string, AssessmentsGrade>;
  onUpdateEntry: (params: UpdateMarkbookForGroupsAssessmentEntryParams) => Promise<void>;
  cellContext: CellContext<AssessmentCellState>;
  middlewareConfig: Record<
    AssessmentMethodType,
    GridCellMiddleware<any, any, AssessmentCellState>[]
  >;
  onSuccess?: () => void;
};

type UpdateAssessmentEntryParamsWithCellId<T> = {
  updateEntryParams: T;
  onUpdateEntry: (params: T) => Promise<void>;
  cellContext: CellContext<AssessmentCellState>;
  middlewareConfig: Record<
    AssessmentMethodType,
    GridCellMiddleware<any, any, AssessmentCellState>[]
  >;
  cellId?: string;
};

export const useRenderMarkbookForGroupsEntryMethodWithMiddleware = ({
  lists,
  onUpdateEntry,
  cellContext,
  middlewareConfig,
  onSuccess,
}: UseRenderMarkbookForGroupsEntryMethodWithMiddlewareProps) => {
  const baseRenderEntryMethod = useRenderBaseMarkbookForGroupsEntryMethod(lists, onSuccess);

  return useCallback(
    (props: RenderMarkbookForGroupsMethodProps) => {
      const updateEntry = (params: UpdateMarkbookForGroupsAssessmentEntryParams) =>
        updateAssessmentEntry({
          updateEntryParams: params,
          onUpdateEntry,
          cellContext,
          middlewareConfig,
          cellId: props.cellId,
        });

      return baseRenderEntryMethod({ ...props, updateAssessmentEntry: updateEntry });
    },
    [baseRenderEntryMethod, onUpdateEntry, cellContext, middlewareConfig],
  );
};

const convertToAssessmentEntries = (entries: MarkbookAssessmentEntry[]): AssessmentEntry[] => {
  return entries.reduce<AssessmentEntry[]>((acc, entry) => {
    const { method } = entry;
    switch (method.type) {
      case AssessmentMethodType.Score:
        const { score } = entry;
        if (typeof score === 'number') {
          acc.push({ method, score });
        }
        break;
      case AssessmentMethodType.Grade:
        const { select_list_option_id } = entry;
        if (typeof select_list_option_id === 'string') {
          acc.push({ method, select_list_option_id });
        }
        break;
      case AssessmentMethodType.Comment: {
        const { comment, creator } = entry;
        if (typeof comment === 'string' && creator) {
          acc.push({
            method,
            comment,
            creator: { ...creator, last_name: creator.last_name ?? '' },
          });
        }
        break;
      }
    }
    return acc;
  }, []);
};

export const getAssessmentEntriesDataFromMarkbookEntries = (
  markbookEntries: MarkbookAssessmentEntry[],
) => {
  const entries = convertToAssessmentEntries(markbookEntries);
  const groupId = markbookEntries[0]?.group.id ?? '';
  const enterableAndMyGroup = markbookEntries[0]?.enterable_and_my_group ?? false;
  return { entries, groupId, enterableAndMyGroup };
};
